import { useState } from "react";
import classNames from "classnames";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

import { toSnakeCase } from "shared/utils";

import Tooltip from "features/components/Tooltip";
import Typography from "features/components/Typography";

import NavBarItem, { ExternalNavBarItem } from "./NavBarItem";
import { NavGroup } from "./types";

interface Props extends NavGroup {
  menuExpanded: boolean;
}

const SideMenuGroup = ({
  title,
  links,
  defaultExpanded = true,
  menuExpanded,
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const navBarGroupHeader = (
    <div
      className={classNames("cursor-pointer tracking-normal", {
        "px-2 py-0.5": menuExpanded,
        "px-1 py-0.5": !menuExpanded,
      })}
    >
      <div
        className={classNames(
          "flex items-center tracking-widest bg-white hover:bg-surface-subtle border border-slate-200 cursor-pointer rounded-md py-2 h-[30px]",
          {
            "pl-4 !py-0 pr-2 justify-between rounded-lg": menuExpanded,
            "justify-center mx-2 w-10": !menuExpanded,
          }
        )}
        onClick={() => setExpanded(!expanded)}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        data-testid={`menu-group-${toSnakeCase(title)}`}
      >
        {menuExpanded && (
          <Typography
            variant="overline"
            className="block uppercase !text-gray-400"
            data-testid="menu-subgroup-title"
          >
            {title}
          </Typography>
        )}
        <span
          className={classNames(
            "transition-transform duration-200 flex items-center",
            {
              "rotate-180": expanded,
            }
          )}
        >
          <ExpandMoreOutlinedIcon />
        </span>
      </div>
    </div>
  );

  return (
    <li>
      {menuExpanded && navBarGroupHeader}
      {!menuExpanded && (
        <Tooltip title={title} placement="right">
          {navBarGroupHeader}
        </Tooltip>
      )}
      <div
        className={classNames(
          "grid origin-top transition-all duration-200 ease-out",
          {
            "grid-rows-[0fr] opacity-0 scale-y-95": !expanded,
            "grid-rows-[1fr] opacity-100 scale-y-100": expanded,
          }
        )}
      >
        <ul className="overflow-hidden">
          {links.map(
            (link) =>
              (link.isExternal && (
                <ExternalNavBarItem
                  key={link.to.toString()}
                  link={link}
                  menuExpanded={menuExpanded}
                />
              )) || (
                <NavBarItem
                  key={link.to.toString()}
                  item={link}
                  menuExpanded={menuExpanded}
                />
              )
          )}
        </ul>
      </div>
    </li>
  );
};

export default SideMenuGroup;
